import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ApolloCache } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
  GET_EVENT_LANDING_PAGE,
  UPDATE_EVENT,
  UPSERT_EVENT_LANDING_PAGE,
} from "@app/graphql/events.queries";
import { createSlug } from "@app/lib/events-admin-utils";
import { encodeEventDescription } from "@app/lib/event-description";
import type {
  NormalizedEvent,
  NormalizedLandingPage,
} from "@app/hooks/use-event-invitation-data";

export type DraftValues = {
  title: string;
  subtitle: string;
  description: string;
  venue: string;
  venueAddress: string;
  startDate: string;
  endDate: string;
};

export type SaveStatus = "idle" | "saved" | "failed";

interface UpdateEventMutationData {
  updateEvent: {
    eventId: string;
    eventName: string;
    description: string;
    venue: string;
    venueAddress: string;
    startDate: string;
    endDate: string;
  } | null;
}

interface UpdateEventMutationVariables {
  eventId: string;
  input: {
    eventName?: string;
    description?: string;
    destination?: string;
    venue?: string;
    venueAddress?: string;
    startDate?: string;
    endDate?: string;
  };
}

interface UpsertLandingPageMutationData {
  upsertEventLandingPage: {
    slug: string;
    title: string;
    subtitle: string | null;
    description: string | null;
    registrationOpen: boolean;
    __typename?: string;
  } | null;
}

interface UpsertLandingPageMutationVariables {
  eventId: string;
  input: {
    slug: string;
    title: string;
    subtitle: string;
    description: string;
    registrationOpen: boolean;
  };
}

/** Inline (non-date) fields that support single-field save. */
type InlineField =
  | "title"
  | "subtitle"
  | "description"
  | "venue"
  | "venueAddress";

/** Subset of inline fields that are mirrored onto the landing page. */
type LandingField = "title" | "subtitle" | "description";

/**
 * Declarative routing for inline (non-date) fields. Adding a new editable field
 * is a data change here instead of editing the save handler (Open/Closed).
 */
type InlineFieldConfig =
  | {
      eventField?: keyof UpdateEventMutationVariables["input"];
      landing: false;
    }
  | {
      eventField?: keyof UpdateEventMutationVariables["input"];
      landing: true;
      landingField: LandingField;
    };

const INLINE_FIELD_WRITE_MAP: Record<InlineField, InlineFieldConfig> = {
  title: { eventField: "eventName", landing: true, landingField: "title" },
  subtitle: { landing: true, landingField: "subtitle" },
  description: {
    eventField: "description",
    landing: true,
    landingField: "description",
  },
  venue: { eventField: "venue", landing: false },
  venueAddress: { eventField: "venueAddress", landing: false },
};

const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const SAVED_RESET_MS = 1200;
const FAILED_RESET_MS = 1800;

type UpsertedLandingPage = NonNullable<
  UpsertLandingPageMutationData["upsertEventLandingPage"]
>;

/**
 * Writes the upserted landing page into the `GET_EVENT_LANDING_PAGE` cache entry
 * so the UI updates without an extra refetch round-trip. The upsert mutation
 * does not return `links`, so the booking links already cached for this event
 * are preserved instead of being dropped.
 */
function writeLandingPageToCache(
  cache: ApolloCache,
  eventId: string,
  updated: UpsertedLandingPage,
): void {
  const cachedLinks =
    cache.readQuery<{
      getEventLandingPage: { links: unknown } | null;
    }>({
      query: GET_EVENT_LANDING_PAGE,
      variables: { eventId },
    })?.getEventLandingPage?.links ?? null;

  cache.writeQuery({
    query: GET_EVENT_LANDING_PAGE,
    variables: { eventId },
    data: {
      getEventLandingPage: {
        __typename: updated.__typename ?? "EventLandingPage",
        ...updated,
        links: cachedLinks,
      },
    },
  });
}

function normalizeDateForMutation(value: string): string | null {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return "";
  }

  if (ISO_DATE_ONLY_REGEX.test(trimmedValue)) {
    return new Date(`${trimmedValue}T00:00:00.000Z`).toISOString();
  }

  const parsedDate = new Date(trimmedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate.toISOString();
}

function getServerValues(
  event: NormalizedEvent,
  landingPage: NormalizedLandingPage | null,
): DraftValues {
  return {
    title: event.name,
    subtitle: landingPage?.subtitle ?? "",
    description: event.description,
    venue: event.venue,
    venueAddress: event.venueAddress,
    startDate: event.startDate,
    endDate: event.endDate,
  };
}

function isInlineField(field: keyof DraftValues): field is InlineField {
  return field in INLINE_FIELD_WRITE_MAP;
}

function isDateField(field: keyof DraftValues): boolean {
  return field === "startDate" || field === "endDate";
}

const EMPTY_DRAFT: DraftValues = {
  title: "",
  subtitle: "",
  description: "",
  venue: "",
  venueAddress: "",
  startDate: "",
  endDate: "",
};

export type UseEventBuilderArgs = {
  event: NormalizedEvent | undefined;
  landingPage: NormalizedLandingPage | null;
  onError?: (error: unknown) => void;
};

export type UseEventBuilderResult = {
  editingField: keyof DraftValues | null;
  draftValues: DraftValues;
  setDraftValues: React.Dispatch<React.SetStateAction<DraftValues>>;
  saveStatus: SaveStatus;
  /** The field currently being persisted, or null when idle. */
  savingField: keyof DraftValues | null;
  /** True while any save is in progress (used to disable other controls). */
  isSaving: boolean;
  /** True while the date range is being persisted (start or end date). */
  isSavingDates: boolean;
  handleSaveInline: (field: keyof DraftValues) => Promise<boolean>;
  handleSaveDates: () => Promise<boolean>;
  handleEditFieldChange: (nextField: keyof DraftValues) => Promise<void>;
};

/**
 * Encapsulates the edit-page state and persistence logic (inline edits, date
 * edits, dual-write to event + landing page). Keeping this out of the page
 * component lets the page focus on rendering (SRP) and keeps mutation logic out
 * of any module that the public landing page imports.
 */
export function useEventBuilder({
  event,
  landingPage,
  onError,
}: UseEventBuilderArgs): UseEventBuilderResult {
  const [updateEventMutation] = useMutation<
    UpdateEventMutationData,
    UpdateEventMutationVariables
  >(UPDATE_EVENT);
  const [upsertLandingPageMutation] = useMutation<
    UpsertLandingPageMutationData,
    UpsertLandingPageMutationVariables
  >(UPSERT_EVENT_LANDING_PAGE);

  const [editingField, setEditingField] = useState<keyof DraftValues | null>(
    null,
  );
  const [draftValues, setDraftValues] = useState<DraftValues>(() =>
    event ? getServerValues(event, landingPage) : EMPTY_DRAFT,
  );
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [savingField, setSavingField] = useState<keyof DraftValues | null>(
    null,
  );
  const saveResetTimeout = useRef<number | null>(null);

  const serverValues = useMemo(
    () => (event ? getServerValues(event, landingPage) : null),
    [event, landingPage],
  );

  const hasChangedDateField = useCallback(
    (serverValue: string, draftValue: string): boolean => {
      const normalizedServerValue = normalizeDateForMutation(serverValue);
      const normalizedDraftValue = normalizeDateForMutation(draftValue);

      if (normalizedServerValue === null || normalizedDraftValue === null) {
        return serverValue !== draftValue;
      }

      return normalizedServerValue !== normalizedDraftValue;
    },
    [],
  );

  useEffect(() => {
    if (!event) {
      return;
    }

    // Re-seed drafts from the server snapshot, but never while the user is
    // actively editing a field (would clobber in-flight edits on refetch).
    if (editingField) {
      return;
    }

    setDraftValues(getServerValues(event, landingPage));
  }, [event, landingPage, editingField]);

  useEffect(
    () => () => {
      if (saveResetTimeout.current !== null) {
        window.clearTimeout(saveResetTimeout.current);
      }
    },
    [],
  );

  const scheduleStatusReset = useCallback((delayMs: number) => {
    if (saveResetTimeout.current !== null) {
      window.clearTimeout(saveResetTimeout.current);
    }
    saveResetTimeout.current = window.setTimeout(() => {
      setSaveStatus("idle");
      saveResetTimeout.current = null;
    }, delayMs);
  }, []);

  const flashSaved = useCallback(() => {
    setSaveStatus("saved");
    scheduleStatusReset(SAVED_RESET_MS);
  }, [scheduleStatusReset]);

  const flashSaveFailed = useCallback(() => {
    setSaveStatus("failed");
    scheduleStatusReset(FAILED_RESET_MS);
  }, [scheduleStatusReset]);

  const buildLandingInput = useCallback(
    (
      overrides: Partial<Record<LandingField, string>>,
    ): UpsertLandingPageMutationVariables["input"] => {
      if (!event) {
        throw new Error("Cannot build landing input without a loaded event");
      }

      const title = overrides.title ?? landingPage?.title ?? event.name;
      const slug =
        landingPage?.slug ||
        createSlug(title) ||
        createSlug(`${event.id}-landing-page`) ||
        event.id;

      return {
        slug,
        title,
        subtitle: overrides.subtitle ?? landingPage?.subtitle ?? "",
        description: encodeEventDescription(
          overrides.description ??
            landingPage?.description ??
            event.description,
        ),
        registrationOpen: landingPage?.registrationOpen ?? true,
      };
    },
    [event, landingPage],
  );

  const getServerValue = useCallback(
    (field: keyof DraftValues): string => serverValues?.[field] ?? "",
    [serverValues],
  );

  const handleSaveInline = useCallback(
    async (field: keyof DraftValues): Promise<boolean> => {
      if (!event || !isInlineField(field)) {
        return false;
      }

      // Skip the network entirely when the value is unchanged: just close the
      // editor and flash "Saved" so no event/landing-page write is issued.
      const unchangedVenueFields =
        field === "venue" &&
        draftValues.venue === getServerValue("venue") &&
        draftValues.venueAddress === getServerValue("venueAddress");
      if (
        unchangedVenueFields ||
        (field !== "venue" && draftValues[field] === getServerValue(field))
      ) {
        setEditingField(null);
        flashSaved();
        return true;
      }

      const writeConfig = INLINE_FIELD_WRITE_MAP[field];
      const eventInput: UpdateEventMutationVariables["input"] = {};
      if (writeConfig.eventField) {
        eventInput[writeConfig.eventField] =
          field === "description"
            ? encodeEventDescription(draftValues[field])
            : draftValues[field];
      }
      if (field === "venue") {
        eventInput.venue = draftValues.venue;
        eventInput.venueAddress = draftValues.venueAddress;
      }

      const landingOverride: Partial<Record<LandingField, string>> | null =
        writeConfig.landing
          ? { [writeConfig.landingField]: draftValues[field] }
          : null;

      setSavingField(field);
      try {
        // The event write and the landing-page write target independent
        // records, so run them in parallel. We rely on Apollo cache
        // normalization (Event by eventId) and an explicit cache write for the
        // landing page, so no follow-up refetch query is needed.
        const saveTasks: Promise<unknown>[] = [];

        if (Object.keys(eventInput).length > 0) {
          saveTasks.push(
            updateEventMutation({
              variables: { eventId: event.id, input: eventInput },
            }),
          );
        }

        if (landingOverride) {
          const landingInput = buildLandingInput(landingOverride);
          saveTasks.push(
            upsertLandingPageMutation({
              variables: { eventId: event.id, input: landingInput },
              update: (cache, { data }) => {
                const updated = data?.upsertEventLandingPage;
                if (updated) {
                  writeLandingPageToCache(cache, event.id, updated);
                }
              },
            }),
          );
        }

        await Promise.all(saveTasks);

        setEditingField(null);
        flashSaved();
        return true;
      } catch (error) {
        onError?.(error);
        flashSaveFailed();
        return false;
      } finally {
        setSavingField(null);
      }
    },
    [
      event,
      draftValues,
      getServerValue,
      updateEventMutation,
      upsertLandingPageMutation,
      buildLandingInput,
      flashSaved,
      flashSaveFailed,
      onError,
    ],
  );

  const handleSaveDates = useCallback(async (): Promise<boolean> => {
    if (!event) {
      return false;
    }

    const normalizedStartDate = normalizeDateForMutation(draftValues.startDate);
    const normalizedEndDate = normalizeDateForMutation(draftValues.endDate);
    if (normalizedStartDate === null || normalizedEndDate === null) {
      flashSaveFailed();
      return false;
    }

    const input: UpdateEventMutationVariables["input"] = {};
    if (hasChangedDateField(event.startDate, draftValues.startDate)) {
      input.startDate = normalizedStartDate;
    }

    if (hasChangedDateField(event.endDate, draftValues.endDate)) {
      input.endDate = normalizedEndDate;
    }

    if (Object.keys(input).length === 0) {
      setEditingField(null);
      flashSaved();
      return true;
    }

    setSavingField("startDate");
    try {
      await updateEventMutation({
        variables: { eventId: event.id, input },
      });

      setEditingField(null);
      flashSaved();
      return true;
    } catch (error) {
      onError?.(error);
      flashSaveFailed();
      return false;
    } finally {
      setSavingField(null);
    }
  }, [
    event,
    draftValues,
    hasChangedDateField,
    updateEventMutation,
    flashSaved,
    flashSaveFailed,
    onError,
  ]);

  const handleEditFieldChange = useCallback(
    async (nextField: keyof DraftValues): Promise<void> => {
      if (!editingField || editingField === nextField) {
        setEditingField(nextField);
        return;
      }

      if (isDateField(editingField) && isDateField(nextField)) {
        setEditingField("startDate");
        return;
      }

      if (isDateField(editingField)) {
        const hasUnsavedDateChanges =
          hasChangedDateField(
            getServerValue("startDate"),
            draftValues.startDate,
          ) ||
          hasChangedDateField(getServerValue("endDate"), draftValues.endDate);

        if (hasUnsavedDateChanges) {
          const isSaved = await handleSaveDates();
          if (!isSaved) {
            return;
          }
        }

        setEditingField(nextField);
        return;
      }

      const hasUnsavedChanges =
        editingField === "venue"
          ? draftValues.venue !== getServerValue("venue") ||
            draftValues.venueAddress !== getServerValue("venueAddress")
          : draftValues[editingField] !== getServerValue(editingField);

      if (hasUnsavedChanges) {
        const isSaved = await handleSaveInline(editingField);
        if (!isSaved) {
          return;
        }
      }

      setEditingField(nextField);
    },
    [
      editingField,
      draftValues,
      getServerValue,
      hasChangedDateField,
      handleSaveDates,
      handleSaveInline,
    ],
  );

  return {
    editingField,
    draftValues,
    setDraftValues,
    saveStatus,
    savingField,
    isSaving: savingField !== null,
    isSavingDates: savingField === "startDate",
    handleSaveInline,
    handleSaveDates,
    handleEditFieldChange,
  };
}
